import { TabView, TabPanel } from "primereact/tabview";
import { WorkoutRow } from "../WorkoutRow";
import { WorkoutPlanDialog } from "../../workoutPlanDialog/WorkoutPlanDialog";
import { WorkoutPlanDialogMode } from "../../workoutPlanDialog/models/workoutPlanViewModel";
import { IconType } from "../../../icons/Icon";
import { DefaultButton } from "../../button/defaultButton/DefaultButton";
import { DismissibleNote } from "../../common/DismissibleNote/DismissibleNote";
import { EmptyStateCard } from "../../common/EmptyStateCard/EmptyStateCard";
import { dateUtility } from "../../../infrastructure/utilities/dateUtility";
import { WorkoutStatistics } from "../../../../components/portal/myWorkouts/statistics/WorkoutStatistics";
import { IUseWorkoutStatisticsArgs } from "../../../../components/portal/myWorkouts/statistics/hooks/useWorkoutStatistics";
import { WorkoutAchievements } from "../../../../components/portal/myWorkouts/achievements/WorkoutAchievements";
import { IUseWorkoutAchievementsArgs } from "../../../../components/portal/myWorkouts/achievements/hooks/useWorkoutAchievements";
import {
  ICreateWorkoutForUser,
  useWorkoutsOverview,
} from "./hooks/useWorkoutsOverview";
import { CreateStandaloneWorkoutDialog } from "./components/CreateStandaloneWorkoutDialog";
import styles from "./WorkoutsOverview.module.scss";

const FALLBACK_NAME = "Тренировка";
const NO_SUBSCRIPTION_TOOLTIP = "Нужен е активен абонамент";

/** The client's own wording; the admin page passes its own via `emptyState`. */
const DEFAULT_EMPTY_STATE = {
  title: "Все още нямате тренировки",
  description:
    "Тук ще се появяват всички ваши тренировки - от групов клас, от лична тренировка с треньор или създадена от вас.",
};

interface IWorkoutsOverviewProps {
  /** Endpoint returning the rich workout list (IMyWorkoutListItemViewModel[]). */
  listEndpoint: string;
  /** React-query key for the list (kept distinct per data source). */
  listQueryKey: unknown[];
  /** Statistics data-source args forwarded to the shared WorkoutStatistics tab. */
  statistics: IUseWorkoutStatisticsArgs;
  /** Achievements data-source args forwarded to the shared WorkoutAchievements tab. */
  achievements: IUseWorkoutAchievementsArgs;
  /** Mode the workout dialog opens in when a row is tapped. */
  dialogMode: WorkoutPlanDialogMode;
  /** Optional subtext shown in the opened workout dialog (e.g. the trainee's name). */
  registeredUserName?: string;
  /**
   * Whose workouts these are, when it isn't the caller (the admin user-profile page): the
   * opened workout is read against this user's history (ПРЕДИШНА, personal records).
   */
  userId?: number;
  /** Show the "+" create-workout button and the empty state's create action. */
  showCreateButton?: boolean;
  /** Whether the caller may actually create (a client needs an active subscription; staff always may). */
  canCreate?: boolean;
  /**
   * Staff flow (the admin user-profile page): the new workout is created for this user
   * through the staff endpoint. Absent, the flow posts to the caller's own CreateStandalone.
   */
  createForUser?: ICreateWorkoutForUser;
  /** Wording of the empty "Тренировки" tab; defaults to the client's own. */
  emptyState?: { title: string; description: string };
  /**
   * Short explanation of what this list is, shown above the workouts tab. The
   * storageKey remembers that the user closed it, so it never comes back.
   */
  intro?: { storageKey: string; title: string; text: string };
}

/**
 * The three-tab workouts experience (workout list + statistics + achievements) shared
 * by the user's own "My Workouts" page and the admin user-profile workouts page. Both
 * feed it a different data source; the rendering — WorkoutRow cards, the statistics
 * tab with its date filter and charts, the personal-record badges, and the
 * WorkoutPlanDialog — is identical. On My Workouts it also offers the subscribed-only
 * "+" create-standalone-workout flow; the admin page offers the same flow for the
 * client, through the staff endpoint.
 */
export const WorkoutsOverview = ({
  listEndpoint,
  listQueryKey,
  statistics,
  achievements,
  dialogMode,
  registeredUserName,
  userId,
  showCreateButton = false,
  canCreate = false,
  createForUser,
  emptyState = DEFAULT_EMPTY_STATE,
  intro,
}: IWorkoutsOverviewProps) => {
  const {
    workouts,
    isLoading,
    activeTabIndex,
    setActiveTabIndex,
    selected,
    setSelected,
    isCreateOpen,
    setIsCreateOpen,
    isCreating,
    requestCreate,
    confirmCreate,
  } = useWorkoutsOverview(listEndpoint, listQueryKey, createForUser);

  const createButton = showCreateButton ? (
    <div className={styles.create_row}>
      <DefaultButton
        className={styles.create_row_button}
        design="outline"
        label="+ Нова тренировка"
        tooltip={canCreate ? undefined : NO_SUBSCRIPTION_TOOLTIP}
        width="auto"
        onClick={() => requestCreate(canCreate)}
      />
    </div>
  ) : null;

  const introNote = intro ? (
    <DismissibleNote
      storageKey={intro.storageKey}
      title={intro.title}
      text={intro.text}
    />
  ) : null;

  const renderWorkoutList = () => {
    if (!workouts?.length && !isLoading) {
      return (
        <div className={styles.empty_wrapper}>
          {introNote}
          <EmptyStateCard
            className={styles.empty_card}
            icon={IconType.Dumbell}
            title={emptyState.title}
            description={emptyState.description}
            actionLabel={showCreateButton ? "Създай тренировка" : undefined}
            actionTooltip={canCreate ? undefined : NO_SUBSCRIPTION_TOOLTIP}
            onAction={
              showCreateButton ? () => requestCreate(canCreate) : undefined
            }
          />
        </div>
      );
    }

    return (
      <div className={styles.list}>
        {introNote}
        {createButton}
        {isLoading
          ? Array(3)
              .fill(0)
              .map((_, index) => (
                <div className={styles.skeleton} key={index} />
              ))
          : workouts?.map((workout) => (
              <WorkoutRow
                key={workout.id}
                workout={{
                  title:
                    workout.workoutName?.trim() ||
                    workout.eventName ||
                    FALLBACK_NAME,
                  date: workout.date,
                  totalVolumeKg: workout.totalVolumeKg,
                  exerciseCount: workout.exerciseCount,
                  exercises: workout.exercises,
                  lineageNumber: workout.lineageNumber,
                }}
                onClick={() =>
                  setSelected({
                    eventId: workout.eventId ?? undefined,
                    planId: workout.id,
                    dateLabel: dateUtility.formatDateTime(workout.date),
                  })
                }
              />
            ))}
      </div>
    );
  };

  return (
    <div className={styles.container}>
      <div className={styles.tabs_wrapper}>
        <TabView
          activeIndex={activeTabIndex}
          onTabChange={(e) => setActiveTabIndex(e.index)}
          className={styles.root_tabs}
        >
          <TabPanel header="Тренировки">{renderWorkoutList()}</TabPanel>
          <TabPanel header="Статистика">
            <WorkoutStatistics {...statistics} />
          </TabPanel>
          <TabPanel header="Постижения">
            <WorkoutAchievements
              {...achievements}
              onOpenWorkout={(achievement) =>
                setSelected({
                  eventId: achievement.eventId ?? undefined,
                  planId: achievement.planId,
                  dateLabel: dateUtility.formatDateTime(achievement.date),
                })
              }
            />
          </TabPanel>
        </TabView>
      </div>

      <WorkoutPlanDialog
        visible={!!selected}
        onHide={() => setSelected(null)}
        eventId={selected?.eventId}
        planId={selected?.planId}
        userId={userId}
        registeredUserName={registeredUserName}
        eventDateLabel={selected?.dateLabel}
        mode={dialogMode}
      />

      <CreateStandaloneWorkoutDialog
        visible={isCreateOpen}
        onHide={() => setIsCreateOpen(false)}
        onConfirm={confirmCreate}
        isCreating={isCreating}
      />
    </div>
  );
};
